import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Alert,
    Chip,
    TextField,
    Paper,
    Autocomplete,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    CircularProgress,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import SchoolIcon from '@mui/icons-material/School';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { internshipDomain } from '../util/domain';
import { debounce } from 'lodash';

const EventList = () => {
    // Temporary variable to show registration closed image
    // Set to true to show the image, false to show regular view
    const SHOW_REGISTRATION_CLOSED_IMAGE = true;

    const [events, setEvents] = useState([]);
    const [error, setError] = useState('');
    const [imageRefreshTimestamp, setImageRefreshTimestamp] = useState(Date.now());
    const navigate = useNavigate();

    // Registration form states
    const [utmParams, setUtmParams] = useState({
        utm_source: null,
        utm_medium: null,
        utm_campaign: null
    });
    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        email: '',
        gender: '',
        otherGender: '',
        degree: '',
        otherDegree: '',
        yop: '',
        otherYear: '',
        collegeName: '',
        college_code: '',
        branch: '',
        otherBranch: '',
        state: '',
        otherState: '',
        city: '',
    });
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [errors, setErrors] = useState({});
    const [formLoading, setFormLoading] = useState(false);
    const [states, setStates] = useState([]);
    const [colleges, setColleges] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isOtherCollegeSelected, setIsOtherCollegeSelected] = useState(false);

    // Refs for "Other" input fields
    const otherYearRef = React.useRef(null);
    const otherStateRef = React.useRef(null);
    const otherDegreeRef = React.useRef(null);
    const otherGenderRef = React.useRef(null);
    const otherBranchRef = React.useRef(null);

    const collegesWithOther = formData.state && formData.state !== 'Other'
        ? [...colleges, { college_name: 'Other', college_code: '' }]
        : colleges;

    useEffect(() => {
        fetchEvents();
        fetchStates();

        // Capture UTM params
        const params = new URLSearchParams(window.location.search);
        setUtmParams({
            utm_source: params.get('utm_source') || null,
            utm_medium: params.get('utm_medium') || null,
            utm_campaign: params.get('utm_campaign') || null
        });
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await axios.get(`${internshipDomain}/api/events`);
            if (response.data.success) {
                setEvents(response.data.events);
            }
        } catch (error) {
            setError('Failed to fetch events');
        }
    };

    const fetchStates = async () => {
        try {
            const response = await axios.get(`${internshipDomain}/api/college/states`);
            if (response.data.success) {
                setStates(response.data.states);
            }
        } catch (error) {
            console.error('Failed to fetch states:', error);
        }
    };

    // Debounced search function for colleges
    const debouncedFetchColleges = useCallback(
        debounce(async (stateId, searchQuery, pageNum) => {
            try {
                const response = await axios.get(
                    `${internshipDomain}/api/college/colleges/${stateId}`,
                    {
                        params: {
                            page: pageNum,
                            limit: 20,
                            search: searchQuery
                        }
                    }
                );
                if (response.data.success) {
                    if (pageNum === 1) {
                        setColleges(response.data.colleges);
                    } else {
                        setColleges(prev => [...prev, ...response.data.colleges]);
                    }
                    setHasMore(pageNum < response.data.pagination.totalPages);
                }
            } catch (error) {
                console.error('Failed to fetch colleges:', error);
            }
        }, 300),
        []
    );

    const fetchColleges = async (stateId, searchQuery = '', pageNum = 1) => {
        setFormLoading(true);
        try {
            await debouncedFetchColleges(stateId, searchQuery, pageNum);
        } finally {
            setFormLoading(false);
        }
    };

    // Generate year options dynamically
    const generateYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = 3; i >= 1; i--) {
            years.push((currentYear - i).toString());
        }
        years.push(currentYear.toString());
        for (let i = 1; i <= 2; i++) {
            years.push((currentYear + i).toString());
        }
        years.push("Other");
        return years;
    };

    const handleFormChange = (e) => {
        if (e.target.name === 'mobile') {
            const cleanedNumber = e.target.value.replace(/\D/g, '');
            const last10Digits = cleanedNumber.slice(-10);
            setFormData({
                ...formData,
                [e.target.name]: last10Digits
            });
            if (last10Digits.length > 0 && last10Digits.length !== 10) {
                setErrors(prev => ({
                    ...prev,
                    mobile: 'Please enter a valid 10-digit mobile number'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    mobile: ''
                }));
            }
        } else if (e.target.name === 'otherYear') {
            const yearValue = e.target.value;
            const cleanedYear = yearValue.replace(/\D/g, '');
            setFormData({
                ...formData,
                [e.target.name]: cleanedYear
            });
            if (cleanedYear.length > 0) {
                if (cleanedYear.length !== 4) {
                    setErrors(prev => ({
                        ...prev,
                        otherYear: 'Year must be exactly 4 digits'
                    }));
                } else {
                    const minYear = new Date().getFullYear() - 10;
                    const maxYear = new Date().getFullYear() + 5;
                    const yearNum = parseInt(cleanedYear, 10);
                    if (yearNum < minYear || yearNum > maxYear) {
                        setErrors(prev => ({
                            ...prev,
                            otherYear: `Please enter a valid year between ${minYear} and ${maxYear}`
                        }));
                    } else {
                        setErrors(prev => ({
                            ...prev,
                            otherYear: ''
                        }));
                    }
                }
            } else {
                setErrors(prev => ({
                    ...prev,
                    otherYear: ''
                }));
            }
        } else {
            setFormData({
                ...formData,
                [e.target.name]: e.target.value,
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!formData.mobile.trim()) {
            newErrors.mobile = 'Mobile number is required';
        } else {
            const cleanedMobile = formData.mobile.replace(/\D/g, '').slice(-10);
            if (cleanedMobile.length !== 10) {
                newErrors.mobile = 'Please enter a valid 10-digit mobile number';
            }
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
        }
        if (formData.gender === 'Other' && !formData.otherGender.trim()) {
            newErrors.otherGender = 'Please specify gender';
        }
        if (!formData.degree) {
            newErrors.degree = 'Degree is required';
        }
        if (formData.degree === 'Other' && !formData.otherDegree.trim()) {
            newErrors.otherDegree = 'Please specify degree';
        }
        if (!formData.yop) {
            newErrors.yop = 'Year of Passing is required';
        }
        if (formData.yop === 'Other' && !formData.otherYear.trim()) {
            newErrors.otherYear = 'Please specify year';
        } else if (formData.yop === 'Other') {
            if (!/^\d{4}$/.test(formData.otherYear)) {
                newErrors.otherYear = 'Year must be exactly 4 digits';
            } else {
                const minYear = new Date().getFullYear() - 10;
                const maxYear = new Date().getFullYear() + 5;
                const yearValue = parseInt(formData.otherYear, 10);
                if (yearValue < minYear || yearValue > maxYear) {
                    newErrors.otherYear = `Please enter a valid year between ${minYear} and ${maxYear}`;
                }
            }
        }
        if (!formData.collegeName.trim()) {
            newErrors.collegeName = 'College name is required';
        }
        if (!formData.branch) {
            newErrors.branch = 'Branch is required';
        }
        if (formData.branch === 'Other' && !formData.otherBranch.trim()) {
            newErrors.otherBranch = 'Please specify branch';
        }
        if (!formData.state) {
            newErrors.state = 'State is required';
        }
        if (formData.state === 'Other' && !formData.otherState.trim()) {
            newErrors.otherState = 'Please specify state';
        }
        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setFormLoading(true);
        setFormError('');
        setFormSuccess('');

        try {
            let finalCollegeName = formData.collegeName;
            let finalCollegeCode = formData.college_code;
            if (
                formData.state !== 'Other' &&
                collegesWithOther.find(c => c.college_name === 'Other') &&
                (collegesWithOther.find(c => c.college_name === formData.collegeName) === undefined || formData.collegeName === '')
            ) {
                finalCollegeCode = null;
            }
            const finalFormData = {
                ...formData,
                state: formData.state === 'Other' ? formData.otherState : formData.state,
                yop: formData.yop === 'Other' ? formData.otherYear : formData.yop,
                collegeName: finalCollegeName,
                college_code: finalCollegeCode,
                utm_source: utmParams.utm_source || null,
                utm_medium: utmParams.utm_medium || null,
                utm_campaign: utmParams.utm_campaign || null
            };

            const response = await axios.post(`${internshipDomain}/api/registration/student/waitlist`, finalFormData);

            if (response.data.success) {
                // Redirect to thank you page
                navigate('/waitlist-thank-you');
            }
        } catch (error) {
            setFormError(error.response?.data?.message || 'Registration failed');
        } finally {
            setFormLoading(false);
        }
    };

    const handleRegister = (eventHashId) => {
        navigate(`/register/${eventHashId}`);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'OPEN':
                return 'success';
            case 'UPCOMING':
                return 'warning';
            case 'CLOSED':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Show only active events
    const activeEvents = events.filter(event => event.is_active);

    const getRegistrationUrl = (hashId) => {
        const origin = window.location.origin;
        return `${origin}/register/${hashId}`;
    };

    // If SHOW_REGISTRATION_CLOSED_IMAGE is true, show only the image
    if (SHOW_REGISTRATION_CLOSED_IMAGE) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL || ''}/registrationClosed.jpg`}
                        alt="Registration Closed"
                        sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            display: 'block'
                        }}
                    />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {activeEvents.length === 0 && (
                    <Box sx={{ mt: 4, mb: 4 }}>
                        {/* Logo */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                            <img
                                src={`${process.env.PUBLIC_URL || ''}/logo-vector.svg`}
                                alt="Vector India Logo"
                                style={{ height: '80px', width: 'auto' }}
                                onError={(e) => {
                                    // Fallback if logo-vector.svg doesn't load
                                    e.target.src = `${process.env.PUBLIC_URL || ''}/vector_logo.jpg`;
                                }}
                            />
                        </Box>

                        <Paper sx={{ p: 4 }}>
                            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 2, color: '#ff8800' }}>
                                Currently No Internship Programs Available
                            </Typography>
                            <Typography variant="h6" gutterBottom align="center" sx={{ mb: 4 }}>
                                Register here for future Internship programs to get notified.
                            </Typography>

                            {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                            {formSuccess && <Alert severity="success" sx={{ mb: 2 }}>{formSuccess}</Alert>}

                            <Box component="form" onSubmit={handleFormSubmit} noValidate>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="name"
                                    label="Please enter your full name."
                                    name="name"
                                    autoComplete="name"
                                    color="warning"
                                    value={formData.name}
                                    onChange={handleFormChange}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                />

                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="mobile"
                                    label="Mobile Number"
                                    name="mobile"
                                    color="warning"
                                    autoComplete="tel"
                                    value={formData.mobile}
                                    onChange={handleFormChange}
                                    error={!!errors.mobile}
                                    helperText={errors.mobile}
                                    inputProps={{
                                        maxLength: 10,
                                        pattern: "[0-9]{10}",
                                        inputMode: "numeric"
                                    }}
                                />

                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="email"
                                    label="Email Address"
                                    name="email"
                                    color="warning"
                                    autoComplete="email"
                                    value={formData.email}
                                    onChange={handleFormChange}
                                    type="email"
                                    error={!!errors.email}
                                    helperText={errors.email}
                                />

                                <Autocomplete
                                    id="gender"
                                    options={["Male", "Female", "Other"]}
                                    value={formData.gender || null}
                                    onChange={(event, newValue) => {
                                        setFormData({
                                            ...formData,
                                            gender: newValue || ''
                                        });
                                        if (newValue === 'Other') {
                                            setTimeout(() => {
                                                otherGenderRef.current?.focus();
                                            }, 0);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="normal"
                                            color="warning"
                                            required
                                            fullWidth
                                            label="Gender"
                                            name="gender"
                                            error={!!errors.gender}
                                            helperText={errors.gender}
                                        />
                                    )}
                                />

                                {formData.gender === 'Other' && (
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="otherGender"
                                        label="Please specify gender"
                                        name="otherGender"
                                        value={formData.otherGender}
                                        color="warning"
                                        onChange={handleFormChange}
                                        error={!!errors.otherGender}
                                        helperText={errors.otherGender}
                                        inputRef={otherGenderRef}
                                    />
                                )}

                                <Autocomplete
                                    id="degree"
                                    options={[
                                        "B.E",
                                        "B.Tech",
                                        "M.E",
                                        "M.Tech",
                                        "BCA",
                                        "MCA",
                                        "B.Sc",
                                        "M.Sc",
                                        "Other"
                                    ]}
                                    value={formData.degree || null}
                                    onChange={(event, newValue) => {
                                        setFormData({
                                            ...formData,
                                            degree: newValue || ''
                                        });
                                        if (newValue === 'Other') {
                                            setTimeout(() => {
                                                otherDegreeRef.current?.focus();
                                            }, 0);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="normal"
                                            color="warning"
                                            required
                                            fullWidth
                                            label="Degree"
                                            name="degree"
                                            error={!!errors.degree}
                                            helperText={errors.degree}
                                        />
                                    )}
                                />

                                {formData.degree === 'Other' && (
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="otherDegree"
                                        label="Please specify degree"
                                        name="otherDegree"
                                        value={formData.otherDegree}
                                        color="warning"
                                        onChange={handleFormChange}
                                        error={!!errors.otherDegree}
                                        helperText={errors.otherDegree}
                                        inputRef={otherDegreeRef}
                                    />
                                )}

                                <FormControl fullWidth margin="normal" required error={!!errors.yop}>
                                    <InputLabel id="yop-label" color="warning">Year of Passing</InputLabel>
                                    <Select
                                        labelId="yop-label"
                                        id="yop"
                                        name="yop"
                                        value={formData.yop || ''}
                                        label="Year of Passing"
                                        color="warning"
                                        onChange={(e) => {
                                            handleFormChange(e);
                                            if (e.target.value === 'Other') {
                                                setTimeout(() => {
                                                    otherYearRef.current?.focus();
                                                }, 0);
                                            }
                                        }}
                                    >
                                        {generateYearOptions().map((year) => (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.yop && <FormHelperText>{errors.yop}</FormHelperText>}
                                </FormControl>

                                {formData.yop === 'Other' && (
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="otherYear"
                                        label="Please specify year (4 digits)"
                                        name="otherYear"
                                        value={formData.otherYear}
                                        color="warning"
                                        onChange={handleFormChange}
                                        inputProps={{
                                            maxLength: 4,
                                            pattern: "[0-9]{4}",
                                            inputMode: "numeric"
                                        }}
                                        error={!!errors.otherYear}
                                        helperText={errors.otherYear}
                                        inputRef={otherYearRef}
                                    />
                                )}

                                <FormControl
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.state}
                                >
                                    <InputLabel id="state-select-label">State</InputLabel>
                                    <Select
                                        labelId="state-select-label"
                                        id="state-select"
                                        color="warning"
                                        value={formData.state}
                                        label="State"
                                        onChange={(e) => {
                                            const selectedValue = e.target.value;
                                            setFormData({
                                                ...formData,
                                                state: selectedValue,
                                                collegeName: ''
                                            });
                                            if (selectedValue !== 'Other') {
                                                const selectedState = states.find(state => state.name === selectedValue);
                                                if (selectedState) {
                                                    fetchColleges(selectedState.id);
                                                }
                                            } else {
                                                setColleges([]);
                                                setTimeout(() => {
                                                    otherStateRef.current?.focus();
                                                }, 0);
                                            }
                                        }}
                                        IconComponent={KeyboardArrowDown}
                                    >
                                        {states.map((state) => (
                                            <MenuItem
                                                key={state.id}
                                                value={state.name}
                                            >
                                                {state.name}
                                            </MenuItem>
                                        ))}
                                        <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                    {errors.state && (
                                        <FormHelperText>{errors.state}</FormHelperText>
                                    )}
                                </FormControl>

                                {formData.state === 'Other' && (
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="otherState"
                                        label="Please specify state"
                                        name="otherState"
                                        value={formData.otherState}
                                        color="warning"
                                        onChange={handleFormChange}
                                        error={!!errors.otherState}
                                        helperText={errors.otherState}
                                        inputRef={otherStateRef}
                                    />
                                )}

                                <Autocomplete
                                    id="collegeName"
                                    options={collegesWithOther}
                                    getOptionLabel={(option) => option.college_name || ''}
                                    loading={formLoading}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (!isOtherCollegeSelected && formData.state && formData.state !== 'Other') {
                                            setSearchText(newInputValue);
                                            const selectedState = states.find(state => state.name === formData.state);
                                            if (selectedState) {
                                                setPage(1);
                                                fetchColleges(selectedState.id, newInputValue, 1);
                                            }
                                        }
                                    }}
                                    filterOptions={(x) => x}
                                    ListboxProps={{
                                        onScroll: (event) => {
                                            const listboxNode = event.currentTarget;
                                            if (
                                                listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50 &&
                                                hasMore &&
                                                !formLoading
                                            ) {
                                                const selectedState = states.find(state => state.name === formData.state);
                                                if (selectedState) {
                                                    const nextPage = page + 1;
                                                    setPage(nextPage);
                                                    fetchColleges(selectedState.id, searchText, nextPage);
                                                }
                                            }
                                        },
                                    }}
                                    value={
                                        formData.state === 'Other'
                                            ? null
                                            : isOtherCollegeSelected
                                                ? { college_name: 'Other', college_code: '' }
                                                : (formData.collegeName && collegesWithOther.length > 0
                                                    ? collegesWithOther.find(college => college.college_name === formData.collegeName) || null
                                                    : null)
                                    }
                                    onChange={(event, newValue) => {
                                        if (newValue && newValue.college_name === 'Other') {
                                            setIsOtherCollegeSelected(true);
                                            setFormData({
                                                ...formData,
                                                collegeName: '',
                                                college_code: ''
                                            });
                                        } else {
                                            setIsOtherCollegeSelected(false);
                                            setFormData({
                                                ...formData,
                                                collegeName: newValue ? newValue.college_name : '',
                                                college_code: newValue ? newValue.college_code : ''
                                            });
                                        }
                                    }}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Box sx={{ maxWidth: '100%', width: '100%' }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: '#FF8800',
                                                        fontSize: '1rem',
                                                        mb: 1,
                                                        textAlign: 'left'
                                                    }}
                                                    noWrap
                                                >
                                                    {option.college_name}
                                                </Typography>
                                                {option.univerisity_name && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <SchoolIcon sx={{ fontSize: '1rem', color: '#000000', mr: 1 }} />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#555555',
                                                                fontSize: '0.875rem',
                                                                flex: 1
                                                            }}
                                                            noWrap
                                                        >
                                                            {option.univerisity_name}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {option.college_address && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <LocationOnIcon sx={{ fontSize: '1rem', color: '#f44336', mr: 1 }} />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#555555',
                                                                fontSize: '0.875rem',
                                                                flex: 1
                                                            }}
                                                            noWrap
                                                        >
                                                            {option.college_address}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {option.college_website && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <LanguageIcon sx={{ fontSize: '1rem', color: '#03a9f4', mr: 1 }} />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#555555',
                                                                fontSize: '0.875rem',
                                                                flex: 1
                                                            }}
                                                            noWrap
                                                        >
                                                            {option.college_website}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                    hidden={!formData.state || formData.state === 'Other'}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="normal"
                                            required
                                            color="warning"
                                            fullWidth
                                            label="College Name"
                                            name="collegeName"
                                            error={!!errors.collegeName}
                                            helperText={errors.collegeName || (!formData.state && 'Please select a state first')}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {formLoading && <CircularProgress color="inherit" size={20} />}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />

                                {formData.state !== 'Other' && isOtherCollegeSelected && (
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="collegeNameOther"
                                        label="Please specify your college name"
                                        name="collegeNameOther"
                                        value={formData.collegeName}
                                        color="warning"
                                        onChange={e => setFormData({ ...formData, collegeName: e.target.value })}
                                        error={!!errors.collegeName}
                                        helperText={errors.collegeName}
                                    />
                                )}

                                <Autocomplete
                                    id="branch"
                                    options={[
                                        "ECE",
                                        "ETC",
                                        "EE",
                                        "EEE",
                                        "EIE",
                                        "CSE",
                                        "AIML",
                                        "AIDS",
                                        "CYBER SECURITY",
                                        "IT",
                                        "Civil",
                                        "Mech",
                                        "Other"
                                    ]}
                                    value={formData.branch || null}
                                    onChange={(event, newValue) => {
                                        setFormData({
                                            ...formData,
                                            branch: newValue || ''
                                        });
                                        if (newValue === 'Other') {
                                            setTimeout(() => {
                                                otherBranchRef.current?.focus();
                                            }, 0);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="normal"
                                            required
                                            fullWidth
                                            label="Branch"
                                            color="warning"
                                            name="branch"
                                            error={!!errors.branch}
                                            helperText={errors.branch}
                                        />
                                    )}
                                />

                                {formData.branch === 'Other' && (
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="otherBranch"
                                        label="Please specify branch"
                                        color="warning"
                                        name="otherBranch"
                                        value={formData.otherBranch}
                                        onChange={handleFormChange}
                                        inputRef={otherBranchRef}
                                        error={!!errors.otherBranch}
                                        helperText={errors.otherBranch}
                                    />
                                )}

                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="city"
                                    label="City/Town"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleFormChange}
                                    color="warning"
                                    error={!!errors.city}
                                    helperText={errors.city}
                                />

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={formLoading}
                                        sx={{
                                            minWidth: 200,
                                            backgroundColor: '#ff6b00',
                                            '&:hover': {
                                                backgroundColor: '#ff8533'
                                            }
                                        }}
                                    >
                                        {formLoading ? 'Submitting...' : 'Register Now'}
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                )}

                <Grid container spacing={3}>
                    {activeEvents.map((event) => (
                        <Grid item xs={12} key={event.hash_id}>
                            <Card>
                                <CardContent>
                                    {/* Display banner if it exists */}
                                    <Box
                                        onClick={() => handleRegister(event.hash_id)}
                                        sx={{
                                            cursor: 'pointer',
                                            mb: 2
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={`${internshipDomain}/workshop/${event.hash_id}.jpeg?v=${imageRefreshTimestamp}`}
                                            key={`${event.hash_id}-${events.length}`}
                                            alt={event.title}
                                            sx={{
                                                width: '100%',
                                                objectFit: 'contain',
                                                borderRadius: 1,
                                                display: 'block',
                                                backgroundColor: '#f5f5f5'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </Box>
                                    {event.html_template && (
                                        < Box
                                            sx={{
                                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                    color: '#ff8800',
                                                    textAlign: 'center'
                                                },
                                                '& p': {
                                                    textAlign: 'center'
                                                },
                                                '& ul': {
                                                    backgroundColor: '#5f5f5f48',
                                                    borderRadius: '0 5px 5px 0',
                                                    minHeight: '250px',
                                                    alignContent: 'center',
                                                    lineHeight: '22px',
                                                    borderLeft: '4px solid #f47c1c'
                                                }
                                            }}
                                            dangerouslySetInnerHTML={{ __html: event.html_template }}
                                        />
                                    )}
                                </CardContent>
                                <CardActions>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => handleRegister(event.hash_id)}
                                        disabled={event.registration_status !== 'OPEN' || event.available_slots <= 0}
                                        sx={{
                                            backgroundColor: '#ff6b00',
                                            '&:hover': {
                                                backgroundColor: '#ff8533'
                                            }
                                        }}
                                    >
                                        {event.registration_status === 'OPEN' ? 'Register Now' : 'Registration Closed'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Container>
    );
};

export default EventList; 